<template>
  <transition name="fade" appear>
    <div @click="goToProduct">
      <MiniProduct
        class="shop-by-widget-product"
        :product="displayedProduct"
        :show-variations="false"
        :favorites="favorites"
        :favoritable="true"
        :full-width="isMobile"
        @favoriteClick="updateFavoriteData"
      />
    </div>
  </transition>
</template>

<script>
import { MiniProduct, MediaUtils } from '@/pixel/index';

import Tracking from '@/components/commons/Tracking.js';
import CountryUtils from '@/components/commons/CountryUtils.js';

import useFavorite from '@/compositions/useFavorite.js';
import LocationService from '@/services/LocationService';

export default {
  name: 'ProductTile',
  components: { MiniProduct },
  props: {
    product: Object,
    tracking: Object,
  },

  setup() {
    const { favorites, updateFavoriteData } = useFavorite();

    return {
      updateFavoriteData,
      favorites,
    };
  },

  data() {
    return {
      selectedProduct: {
        id: null,
        productImageKey: null,
      },
      favorite: false,
      productHover: false,
      productData: { id: this.product.id, ...this.product.attributes },
    };
  },

  computed: {
    isMobile() {
      return MediaUtils.isMobileAndSmaller;
    },

    displayedProduct() {
      const { salePrice, basePrice, skuNo } = this.productData;
      return {
        ...this.productData,
        productImage: {
          url: this.imgUrl,
        },
        isOnSale: this.hasSalePrice(salePrice),
        originalPrice: this.getPrice(basePrice),
        price: this.hasSalePrice(salePrice) ? this.getPrice(salePrice) : this.getPrice(basePrice),
        skuNo,
      };
    },

    imgUrl() {
      return `${this.productData.imageUrl}?fit=max&w=450&q=50`;
    },

    title() {
      return this.productData.title;
    },

    price() {
      if (this.productData.salePrice[LocationService.getCurrencyId()]) {
        return this.formatCurrency(this.productData.salePrice[LocationService.getCurrencyId()]);
      }
      return this.formatCurrency(this.productData.basePrice[LocationService.getCurrencyId()]);
    },
  },

  watch: {
    selectedProduct: {
      handler(product) {
        if (product) this.checkFavorite(product);
      },
    },

    favorites: {
      deep: true,
      immediate: true,
      handler(value, oldValue) {
        if (value) this.checkFavorite(this.selectedProduct);
      },
    },
  },

  beforeMount() {
    this.selectedProduct = { ...this.productData };
    this.selectedProduct.id = +this.selectedProduct.id;
    this.selectedProduct.skuNo = `SKU${this.selectedProduct.id}`;
    this.checkFavorite(this.selectedProduct);
  },

  mounted() {
    if (this.isMobile) this.productHover = true;
  },

  methods: {
    checkFavorite(product) {
      if (product) {
        if (this.favorites) this.favorite = product.id in this.favorites;
        else this.favorite = false;
      }
    },

    hasSalePrice(obj) {
      return obj[LocationService.getCurrencyId()] != null;
    },

    getPrice(obj) {
      return this.formatCurrency(obj[LocationService.getCurrencyId()]);
    },

    goToProduct() {
      const tracked = this.tracking;
      tracked.product = this.displayedProduct;

      Tracking.track('RoomIdeasProductTagView', tracked);
      Tracking.track('room-ideas-image-view', { image_number: this.tracking.index, id: this.tracking.file });

      if (this.isMobile && window.location?.pathname === '/') {
        Tracking.track('image_tagging_product_click', {
          channel: 'LIST',
          image_number: tracked.index,
          room: tracked.room,
          room_images_num: tracked.roomImagesNum,
          id: tracked.file,
          variant: 0,
        });
      }

      window.location.href = this.productData.url;
    },

    formatCurrency(amount) {
      const formatter = new Intl.NumberFormat('en-US');

      const currencyPrefix = CountryUtils.getCountryByCurrencyId(LocationService.getCurrencyId()).currencySymbol;
      return `${currencyPrefix}${formatter.format(amount / 100)}`;
    },
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';

.shop-by-widget-product {
  cursor: pointer;
  @media (min-width: $mobile-breakpoint) {
    margin-top: 15px;
    max-width: 350px;
    margin-right: 20px;
  }

  .product-link {
    pointer-events: none;
  }
}
</style>
